import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  Typography
} from "@mui/material";
import Grid from "@mui/material/Unstable_Grid2";
import {
  fetchNetworkIdentifierConfig,
  updateNetworkIdentifierConfig,
  NetworkIdentifierConfigResponse
} from "./networksAPI";

// Roles the identifier config supports (mirrors the load-network-identifiers
// ROLE_COLUMNS). Admin (institutional) is intentionally excluded.
// Shared with EditNetwork so the details form and this section line up.
export const EDIT_NETWORK_MAX_WIDTH = 1100;

const CONFIG_ROLE_NAMES = ["Trainee", "Attending", "ProgramAdmin"];
const ROLE_LABEL: Record<string, string> = {
  Trainee: "Trainee",
  Attending: "Attending",
  ProgramAdmin: "Program Admin"
};

type RoleState = "none" | "optional" | "required";
// per identifierId -> per role name -> state
type EditState = Record<string, Record<string, RoleState>>;

/**
 * SE-2251: SysAdmin editor for which identifiers apply to a network and whether
 * each is required per role. Self-contained (loads + saves its own config via
 * the /network/{id}/identifier-config endpoints), rendered as a section on the
 * Edit Network page.
 */
export const NetworkIdentifiersEditor = ({
  networkId
}: {
  networkId: string;
}) => {
  const { t } = useTranslation();
  const [data, setData] = useState<NetworkIdentifierConfigResponse | null>(
    null
  );
  const [state, setState] = useState<EditState>({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  const [toAdd, setToAdd] = useState("");
  // identifierId -> # users in this network with a value (guards removal)
  const [userCountById, setUserCountById] = useState<Record<string, number>>(
    {}
  );
  const [confirmRemoveId, setConfirmRemoveId] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        const resp = await fetchNetworkIdentifierConfig(networkId);
        if (cancelled) return;
        const initial: EditState = {};
        for (const item of resp.config) {
          initial[item.identifierId] = {};
          for (const r of item.roles) {
            initial[item.identifierId][r.programRole] = r.required
              ? "required"
              : "optional";
          }
        }
        setData(resp);
        setState(initial);
        setUserCountById(
          Object.fromEntries(
            resp.config.map((i) => [i.identifierId, i.userCount ?? 0])
          )
        );
      } catch (e: any) {
        if (!cancelled) setError(String(e?.message || e));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [networkId]);

  const roles = (data?.roles || []).filter((r) =>
    CONFIG_ROLE_NAMES.includes(r.name)
  );
  const roleIdByName = Object.fromEntries(roles.map((r) => [r.name, r.id]));

  // An identifier is "applied" to this network when it has at least one role set.
  const includedIds = Object.keys(state).filter((id) =>
    Object.values(state[id] || {}).some((s) => s !== "none")
  );
  const availableById = new Map(
    (data?.availableIdentifiers || []).map((i) => [i.id, i])
  );
  const unapplied = (data?.availableIdentifiers || []).filter(
    (i) => !includedIds.includes(i.id)
  );

  // Any identifier starts optional for every role; the admin tunes it per role.
  const addIdentifier = (identifierId: string) => {
    if (!identifierId) return;
    setSaved(false);
    const roleState: Record<string, RoleState> = {};
    for (const roleName of CONFIG_ROLE_NAMES) roleState[roleName] = "optional";
    setState((prev) => ({ ...prev, [identifierId]: roleState }));
  };

  const removeIdentifier = (identifierId: string) => {
    setSaved(false);
    setState((prev) => ({ ...prev, [identifierId]: {} }));
  };

  const setRoleState = (
    identifierId: string,
    roleName: string,
    value: RoleState
  ) => {
    setSaved(false);
    setState((prev) => ({
      ...prev,
      [identifierId]: { ...(prev[identifierId] || {}), [roleName]: value }
    }));
  };

  const onSave = async () => {
    setSaving(true);
    setError(null);
    setSaved(false);
    try {
      const items = Object.entries(state)
        .map(([identifierId, roleStates]) => ({
          identifierId,
          roles: Object.entries(roleStates)
            .filter(([, s]) => s !== "none")
            .map(([roleName, s]) => ({
              programRoleId: roleIdByName[roleName],
              required: s === "required"
            }))
            .filter((r) => !!r.programRoleId)
        }))
        .filter((i) => i.roles.length > 0);
      const resp = await updateNetworkIdentifierConfig(networkId, items);
      // Rebuild state from the authoritative response.
      const next: EditState = {};
      for (const item of resp.config) {
        next[item.identifierId] = {};
        for (const r of item.roles) {
          next[item.identifierId][r.programRole] = r.required
            ? "required"
            : "optional";
        }
      }
      setState(next);
      setUserCountById(
        Object.fromEntries(
          resp.config.map((i) => [i.identifierId, i.userCount ?? 0])
        )
      );
      setSaved(true);
    } catch (e: any) {
      setError(String(e?.message || e));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ mt: 4 }}>
        <Typography variant="h6">{t("Identifiers")}</Typography>
        <CircularProgress size={20} sx={{ mt: 1 }} />
      </Box>
    );
  }

  return (
    <Box sx={{ mt: 4, maxWidth: EDIT_NETWORK_MAX_WIDTH }}>
      <Typography variant="h6">{t("Identifiers")}</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {t(
          "Choose which identifiers apply to this network and whether each is required per role. These drive the fields shown when adding or editing users in this network's programs."
        )}
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      {saved && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {t("Identifier configuration saved.")}
        </Alert>
      )}

      {includedIds.length === 0 ? (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          {t("No identifiers apply to this network yet. Add one below.")}
        </Typography>
      ) : (
        <Stack spacing={1.5}>
          {includedIds.map((id) => {
            const idf = availableById.get(id);
            if (!idf) return null;
            return (
              <Paper key={id} variant="outlined" sx={{ p: 2 }}>
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                >
                  <Typography sx={{ fontWeight: 600 }}>
                    {idf.displayName}
                  </Typography>
                  <Button
                    size="small"
                    color="error"
                    onClick={() => setConfirmRemoveId(id)}
                  >
                    {t("Remove")}
                  </Button>
                </Stack>
                <Grid container spacing={2} sx={{ mt: 1 }}>
                  {roles.map((role) => (
                    <Grid key={role.id} xs={12} sm={4}>
                      <TextField
                        select
                        fullWidth
                        size="small"
                        label={t(ROLE_LABEL[role.name] || role.name)}
                        value={state[id]?.[role.name] || "none"}
                        onChange={(e) =>
                          setRoleState(
                            id,
                            role.name,
                            e.target.value as RoleState
                          )
                        }
                      >
                        <MenuItem value="none">{t("Not applicable")}</MenuItem>
                        <MenuItem value="optional">{t("Optional")}</MenuItem>
                        <MenuItem value="required">{t("Required")}</MenuItem>
                      </TextField>
                    </Grid>
                  ))}
                </Grid>
              </Paper>
            );
          })}
        </Stack>
      )}

      {unapplied.length > 0 && (
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 2 }}>
          <Select
            size="small"
            displayEmpty
            value={toAdd}
            onChange={(e) => setToAdd(e.target.value)}
            sx={{ flex: { xs: 1, sm: "0 1 auto" }, minWidth: { sm: 220 } }}
          >
            <MenuItem value="">{t("Add an identifier…")}</MenuItem>
            {unapplied.map((i) => (
              <MenuItem key={i.id} value={i.id}>
                {i.displayName}
              </MenuItem>
            ))}
          </Select>
          <Button
            variant="outlined"
            disabled={!toAdd}
            onClick={() => {
              const idf = availableById.get(toAdd);
              if (idf) addIdentifier(idf.id);
              setToAdd("");
            }}
          >
            {t("Add")}
          </Button>
        </Stack>
      )}

      <Box sx={{ mt: 3, pb: 4 }}>
        <Button variant="contained" onClick={onSave} disabled={saving}>
          {saving ? t("Saving…") : t("Save Identifiers")}
        </Button>
      </Box>

      <Dialog open={!!confirmRemoveId} onClose={() => setConfirmRemoveId(null)}>
        <DialogTitle>{t("Remove identifier?")}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t(
              "This identifier will no longer apply to this network — users in this network's programs will no longer be asked for it. The change takes effect when you click Save Identifiers."
            )}
          </DialogContentText>
          {confirmRemoveId && (userCountById[confirmRemoveId] || 0) > 0 && (
            <DialogContentText sx={{ mt: 1 }}>
              {userCountById[confirmRemoveId]}{" "}
              {t(
                "user(s) in this network currently have a value for this identifier. Removing it will NOT delete their data — the network just stops asking for it."
              )}
            </DialogContentText>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmRemoveId(null)}>
            {t("Cancel")}
          </Button>
          <Button
            color="error"
            onClick={() => {
              if (confirmRemoveId) removeIdentifier(confirmRemoveId);
              setConfirmRemoveId(null);
            }}
          >
            {t("Remove")}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default NetworkIdentifiersEditor;

/** Read-only summary of a network's identifier config, for the view page. */
export const NetworkIdentifiersView = ({
  networkId
}: {
  networkId: string;
}) => {
  const { t } = useTranslation();
  const [config, setConfig] = useState<
    NetworkIdentifierConfigResponse["config"] | null
  >(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const resp = await fetchNetworkIdentifierConfig(networkId);
        if (!cancelled) setConfig(resp.config);
      } catch {
        if (!cancelled) setConfig([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [networkId]);

  return (
    <Box sx={{ mt: 2 }}>
      <Typography variant="h6">{t("Identifiers")}</Typography>
      {config === null ? (
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 1 }}>
          <CircularProgress size={16} />
          <Typography variant="body2" color="text.secondary">
            {t("Loading identifiers…")}
          </Typography>
        </Stack>
      ) : config.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          {t("No identifiers configured for this network.")}
        </Typography>
      ) : (
        <ul>
          {config.map((item) => {
            const parts = item.roles
              .filter((r) => CONFIG_ROLE_NAMES.includes(r.programRole))
              .map(
                (r) =>
                  `${ROLE_LABEL[r.programRole] || r.programRole}: ${
                    r.required ? t("Required") : t("Optional")
                  }`
              );
            return (
              <li key={item.identifierId}>
                {item.displayName}
                {parts.length ? ` — ${parts.join(", ")}` : ""}
              </li>
            );
          })}
        </ul>
      )}
    </Box>
  );
};
